import { LayoutGrid, Check, ExternalLink, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '../lib/cn.js';

export type SwitchableModule = 'pilot' | 'progress' | 'insight' | 'admin';

interface AppDefinition {
  id: SwitchableModule;
  label: string;
  description: string;
}

const APPS: AppDefinition[] = [
  { id: 'pilot', label: 'PilotPulse', description: 'Pilot programs & reports' },
  { id: 'progress', label: 'ProgressPulse', description: 'Vocabulary & assessments' },
  { id: 'insight', label: 'InsightPulse', description: 'Cross-program analytics' },
  { id: 'admin', label: 'Admin', description: 'Platform administration' },
];

export interface AppSwitcherProps {
  /** The module of the app this switcher is rendered inside. */
  currentModule: SwitchableModule;
  /** Modules the user's tenant has licensed (from /auth/me enabledModules). */
  enabledModules: string[];
  /** Platform admins can reach every app, including Admin. */
  isPlatformAdmin?: boolean;
  /** Absolute origins of each module app; entries without a URL are hidden. */
  appUrls: Partial<Record<SwitchableModule, string | undefined>>;
  /**
   * Performs the SSO handoff to the target module (mint code + redirect).
   * Provided by the host app so this component stays free of API/env coupling.
   */
  onSwitch: (target: SwitchableModule) => void | Promise<void>;
  className?: string;
}

/**
 * Cross-module app switcher (A8). Lists the other module apps the current user
 * can reach and, on selection, delegates to `onSwitch` which mints a single-use
 * SSO code and redirects — so the user lands authenticated without a second
 * login. Renders nothing when there is only one reachable app.
 */
export function AppSwitcher({
  currentModule,
  enabledModules,
  isPlatformAdmin,
  appUrls,
  onSwitch,
  className,
}: AppSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<SwitchableModule | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const available = APPS.filter((appDef) => {
    if (!appUrls[appDef.id]) return false;
    if (appDef.id === 'admin') return !!isPlatformAdmin;
    if (isPlatformAdmin) return true;
    return enabledModules.includes(appDef.id);
  });

  // Nothing to switch to (or only the current app is reachable).
  if (available.length <= 1) return null;

  async function handleSwitch(target: SwitchableModule) {
    if (target === currentModule || busy) return;
    setBusy(target);
    try {
      await onSwitch(target);
      // On success the browser navigates away; keep the spinner until then.
    } catch {
      setBusy(null);
    }
  }

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch app"
        data-testid="app-switcher"
      >
        <LayoutGrid className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Apps</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-64 rounded-lg border bg-white py-1 shadow-lg"
        >
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Switch app
          </div>
          {available.map((appDef) => {
            const isCurrent = appDef.id === currentModule;
            return (
              <button
                key={appDef.id}
                type="button"
                role="menuitem"
                disabled={isCurrent || busy !== null}
                onClick={() => handleSwitch(appDef.id)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:cursor-default disabled:opacity-70',
                  isCurrent && 'bg-gray-50',
                )}
                data-testid={`app-switcher-${appDef.id}`}
              >
                <span className="min-w-0">
                  <span className="block font-medium text-gray-900">{appDef.label}</span>
                  <span className="block truncate text-xs text-gray-500">{appDef.description}</span>
                </span>
                {isCurrent ? (
                  <Check className="h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
                ) : busy === appDef.id ? (
                  <Loader2
                    className="h-4 w-4 shrink-0 animate-spin text-gray-400"
                    aria-hidden="true"
                  />
                ) : (
                  <ExternalLink className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
